import { useState, useEffect } from "react";
import { X, Loader2, Building2, CheckCircle2, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const fld = "eyw-input w-full";
const SIZES = ["5–10", "11–25", "26–50", "51–100", "100+"];

export const CorporateInquiryModal = ({ onClose }) => {
  const [form, setForm] = useState({ company: "", contact_name: "", email: "", phone: "", group_size: "", interest: "", timeframe: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/corporate/inquiries", form);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Something went wrong — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-[#1A242B]/60 backdrop-blur-sm" onClick={onClose} data-testid="corporate-inquiry-modal">
      <div className="bg-[#F9F7F3] rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-7 md:p-8">
          <div className="flex items-start justify-between mb-1">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D98E73] flex items-center gap-2"><Building2 size={14} /> For Teams & Organizations</p>
            <button onClick={onClose} data-testid="corporate-inquiry-close" aria-label="Close" className="text-[#8FA89B] hover:text-[#1A242B] -mt-1"><X size={20} /></button>
          </div>

          {done ? (
            <div className="text-center py-10" data-testid="corporate-inquiry-success">
              <CheckCircle2 className="mx-auto text-[#5C8A72] mb-4" size={40} />
              <h3 className="font-serif text-2xl text-[#1A242B]">Thank you — we've received it.</h3>
              <p className="text-[#4A5D6B] mt-3 leading-relaxed">Our team will reach out to {form.email} shortly to shape the right experience for your group.</p>
              <button onClick={onClose} className="eyw-btn mt-7" data-testid="corporate-inquiry-done-btn">Close</button>
            </div>
          ) : (
            <>
              <h3 className="font-serif text-2xl md:text-3xl text-[#1A242B] tracking-tight">Tell us about your group</h3>
              <p className="text-sm text-[#4A5D6B] mt-2 mb-6">A few details and we'll craft a wellness day, team experience, or retreat around your people.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-xs text-[#4A5D6B] mb-1 block">Company / organization *</label>
                  <input value={form.company} onChange={upd("company")} required data-testid="corporate-company" className={fld} placeholder="Acme Co., Riverside School, family reunion…" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-[#4A5D6B] mb-1 block">Your name *</label>
                    <input value={form.contact_name} onChange={upd("contact_name")} required data-testid="corporate-contact-name" className={fld} />
                  </div>
                  <div>
                    <label className="text-xs text-[#4A5D6B] mb-1 block">Email *</label>
                    <input type="email" value={form.email} onChange={upd("email")} required data-testid="corporate-email" className={fld} />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#4A5D6B] mb-1 block">Callback phone (optional)</label>
                  <input value={form.phone} onChange={upd("phone")} data-testid="corporate-phone" className={fld} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-[#4A5D6B] mb-1 block">Group size</label>
                    <select value={form.group_size} onChange={upd("group_size")} data-testid="corporate-group-size" className={fld}>
                      <option value="">Select…</option>
                      {SIZES.map((s) => <option key={s} value={s}>{s} people</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-[#4A5D6B] mb-1 block">Preferred dates / timeframe</label>
                    <input value={form.timeframe} onChange={upd("timeframe")} data-testid="corporate-timeframe" className={fld} placeholder="e.g. a Friday in November" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#4A5D6B] mb-1 block">What are you looking for? *</label>
                  <textarea value={form.interest} onChange={upd("interest")} required rows={4} data-testid="corporate-interest" className={fld}
                    placeholder="A wellness day for our team of 30, a leadership retreat, monthly on-site sessions…" />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="corporate-inquiry-error">{error}</p>}
                <button type="submit" disabled={busy} data-testid="corporate-inquiry-submit" className="eyw-btn w-full justify-center disabled:opacity-60">
                  {busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={15} />} Send Inquiry
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CorporateInquiryModal;
